// Telemetry strip — a bottom panel showing one row of live readings about a
// single selected thing, whatever that thing happens to be.
//
// Deliberately generic. It knows nothing about aircraft: a caller hands it a
// title and a list of metrics, and it draws them. The first use is the selected
// OGN aircraft (speed, heading, altitude, vertical speed), and the own-vessel
// AIS target and the device's own position are meant to follow — each of those
// is the same shape of problem, a moving subject with a handful of numbers that
// change continuously.
//
// Bottom-panel mechanics are the crossSection.js / weatherCameras.js pattern:
// `hidden` is removed once at build time and the height-0 / `.open` CSS
// transition does the show/hide, with `--timecontrol-height` in radar.css making
// room so the floating buttons ride above it.
//
// WHY A STRIP RATHER THAN A CARD ANCHORED TO THE SUBJECT. The subject moves. An
// overlay pinned to it drifts across the map, needs repositioning on every
// update, and slides off screen exactly when the numbers matter. A fixed strip
// stays where the eye last found it while the map pans underneath.
//
// ONE PANEL, MANY POSSIBLE SOURCES. Only one subject can be displayed at a time,
// so every call carries an `owner` token. A source may only update or close what
// it currently owns — otherwise a background source (say a geolocation tick)
// would quietly wipe out the aircraft the user just selected. Opening always
// takes over, because opening is an explicit user action.

// A reading's unit, set smaller than its number. Every source here already
// writes the two joined by a THIN SPACE (`${value}\u2009${unit}` — see `one()`
// in gliders.js and `fmt()` in metarLayer.js), and that thin space exists
// precisely because the two belong together, so it is a reliable seam: split on
// the last one and what follows is the unit.
//
// Which means nothing needs a new field, and values that carry no unit are left
// exactly as they are — a bare '–', 'CAVOK', 'tyyni', a bearing whose degree
// sign is bound to the number without a space, or the two-word 'ei estettä',
// which has an ordinary space rather than a thin one and so is not touched.
//
// The point is width as much as typography: on a 390 px phone the strip's
// columns are 68 px wide and a value like '571.2 km²' rendered at 78, spilling
// over its neighbours. The number is what the eye reads; the unit only has to
// be legible.
const THIN_SPACE = '\u2009';
function withUnit(value) {
  const text = String(value == null ? '' : value);
  const at = text.lastIndexOf(THIN_SPACE);
  if (at < 0) return text;
  return `${text.slice(0, at)}<span class="telemetry-unit">${text.slice(at + 1)}</span>`;
}

export default function initTelemetryPanel({ container } = {}) {
  if (!container) {
    // Same degraded-but-harmless contract as crossSection.js: with no container
    // the API exists and does nothing, so callers need no guards.
    return {
      open() {}, update() {}, close() {}, isOpen: () => false, ownerIs: () => false,
    };
  }

  container.removeAttribute('hidden');
  container.removeAttribute('aria-hidden');
  container.innerHTML = `
    <div class="telemetry-head">
      <i class="material-icons telemetry-icon" aria-hidden="true"></i>
      <span class="telemetry-title"></span>
      <span class="telemetry-sub"></span>
      <span class="telemetry-status"></span>
      <button type="button" class="telemetry-close" aria-label="Sulje">
        <i class="material-icons" aria-hidden="true">close</i>
      </button>
    </div>
    <div class="telemetry-row"></div>
  `;

  const iconEl = container.querySelector('.telemetry-icon');
  const titleEl = container.querySelector('.telemetry-title');
  const subEl = container.querySelector('.telemetry-sub');
  const statusEl = container.querySelector('.telemetry-status');
  const rowEl = container.querySelector('.telemetry-row');

  let owner = null;
  // Supplied by whoever opened the panel. Closing is not just hiding a box: the
  // source usually has map state tied to it — the aircraft layer's selection
  // ring, say — and only the source knows what that is. So it hands over its own
  // teardown rather than the panel guessing, or radar.js wiring every source in.
  let ownerClose = null;

  function close(from) {
    // A source that no longer owns the panel must not be able to shut it.
    if (from !== undefined && from !== owner) return;
    const teardown = ownerClose;
    owner = null;
    ownerClose = null;
    container.classList.remove('open');
    if (typeof teardown === 'function') teardown();
  }

  container.querySelector('.telemetry-close').addEventListener('click', (e) => {
    e.stopPropagation();
    close();
  });

  // `metrics` is [{ label, value, tone }] — `value` already formatted, because
  // only the caller knows whether a number is knots or metres or degrees, and
  // whether an absent reading should read '–' or be dropped. Its unit, if the
  // caller bound one to the number with a thin space, is set smaller here. `tone` optionally
  // colours the value ('up', 'down', 'warn') for readings whose sign carries
  // meaning, a glider's vertical speed being the motivating case.
  function paint({
    icon, title, subtitle, status, metrics,
  }) {
    if (icon !== undefined) iconEl.textContent = icon;
    if (title !== undefined) titleEl.textContent = title;
    // Never hidden: this element is also the flexible spacer that holds the
    // status and the close button against the right edge, so removing it from
    // the layout when empty would let them drift inward.
    if (subtitle !== undefined) subEl.textContent = subtitle || '';
    // How current the readings are. Every subject this panel is meant to serve
    // reports intermittently — a glider circling in a thermal can go 60-90 s
    // between fixes, and an AIS vessel or a phone's GPS is no different — so the
    // age of the reading belongs beside it, or the numbers read as live when
    // they are not.
    if (status !== undefined) {
      statusEl.textContent = status || '';
      statusEl.hidden = !status;
    }
    if (!metrics) return;
    // Rebuilt wholesale rather than diffed: a strip holds a handful of cells and
    // updates about once a second, so the simple thing is also the fast thing.
    rowEl.innerHTML = metrics.map((m) => `
      <div class="telemetry-cell">
        <span class="telemetry-value${m.tone ? ` is-${m.tone}` : ''}">${withUnit(m.value)}</span>
        <span class="telemetry-label">${m.label}</span>
      </div>`).join('');
  }

  return {
    // Opening always takes over — it is an explicit user action.
    // Taking the panel over runs the previous owner's teardown first, so a
    // source never keeps map state alive for a subject that is no longer shown.
    open(from, payload, onOwnerClose) {
      const previous = ownerClose;
      if (previous && owner !== from) previous();
      owner = from;
      ownerClose = typeof onOwnerClose === 'function' ? onOwnerClose : null;
      paint(payload);
      container.classList.add('open');
    },
    // Silently ignored when another source has taken the panel over, which is
    // what keeps a background feed from redrawing someone else's subject.
    update(from, payload) {
      if (from !== owner) return;
      paint(payload);
    },
    close,
    isOpen: () => container.classList.contains('open'),
    ownerIs: (from) => owner === from,
  };
}
